"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var d3 = require("d3");
var _ = require("lodash");
var randomstring = require("randomstring");
var d3_1 = require("d3");
var component_1 = require("../component");
var geometry_1 = require("../geometry");
var log_1 = require("../log");
var tool = require("../tool");
var __1 = require("..");
var global_1 = require("../global");
var utils_1 = require("../template/utils");
var logger = log_1.getLogger("prototype");
var eventNameMap = {
    _$mouseEnter: "mouseenter",
    _$mouseLeave: "mouseleave",
    _$mouseMove: "mousemove",
    _$mouseClick: "click",
    _$mouseDown: "mousedown",
    _$mouseUp: "mouseup",
};
function parseGeometryValue(value) {
    if (typeof value === "object") {
        if (value.value !== undefined) {
            return value;
        }
        return { value: 0, offset: 0, unit: geometry_1.GeometryUnit.Pixel };
    }
    else if (typeof value === "number") {
        return { value: value, unit: geometry_1.GeometryUnit.Pixel, offset: 0 };
    }
    else {
        return utils_1.parseGeometry(value);
    }
}
var RenderHelper = /** @class */ (function () {
    function RenderHelper(element) {
        this.element = element;
    }
    RenderHelper.prototype.op = function (key, isFunc) {
        if (isFunc === void 0) { isFunc = false; }
        return this.element.getOption(key, this.data, this.index, this.group, isFunc);
    };
    RenderHelper.prototype.ancOp = function (key, isFunc) {
        if (isFunc === void 0) { isFunc = false; }
        return this.element.ancestor.getOption(key, this.data, this.index, this.group, isFunc);
    };
    RenderHelper.prototype.parOp = function (key, isFunc) {
        if (isFunc === void 0) { isFunc = false; }
        return this.element.parent.getOption(key, this.data, this.index, this.group, isFunc);
    };
    RenderHelper.prototype.geo = function (key, isHorizontal) {
        if (isHorizontal === void 0) { isHorizontal = false; }
        return this.element._getGeometry(key, isHorizontal, this.data, this.index, this.group);
    };
    RenderHelper.prototype.set = function (key, value) {
        var vz = this.element.$visualizer;
        var local = vz.locals[key];
        if (typeof local === "undefined") {
            local = vz.locals[key] = d3.local();
        }
        local.set(this.group[this.index], value);
    };
    RenderHelper.prototype.get = function (key) {
        var local = this.element.$visualizer.locals[key];
        if (typeof local === "undefined") {
            // console.error(`You are trying to get an undefined local ${key}.`);
            return undefined;
        }
        return local.get(this.group[this.index]);
    };
    RenderHelper.prototype.setG = function (key, value) {
        this.element.groupLocals[key] = value;
    };
    RenderHelper.prototype.getG = function (key, defaultValue) {
        return this.element.groupLocals[key] || defaultValue;
    };
    RenderHelper.prototype.accG = function (key, value) {
        var currValue = this.getG(key, 0);
        this.setG(key, value + currValue);
        return currValue;
    };
    return RenderHelper;
}());
exports.RenderHelper = RenderHelper;
var PrototypeElement = /** @class */ (function () {
    function PrototypeElement() {
        this.supportsGroup = true;
        this._dirtyOptions = new Set();
        this._appended = false;
        this._dataAssigned = false;
        this._rendered = false;
        this._isEnterAppend = false;
        this.hasFixedSize = true;
        this.hasFlexibleSize = false;
        this.hasAbsolutePosition = false;
        this.__stopRender = false;
        this._geometryKeys = ["x", "y", "width", "height"];
        this._cachedGeometry = [];
        this._cachedAnchor = [];
        this._intrinsicSize = [];
        this._computedSize = [];
        this.noNeedToRender = false;
        this.groupLocals = {};
        this._options = {
            _x: geometry_1.GeometryValue.zero,
            _y: geometry_1.GeometryValue.zero,
            _width: geometry_1.GeometryValue.auto,
            _height: geometry_1.GeometryValue.auto,
            _relatedTo: null,
            _isChild: true,
            _display: true,
        };
        this._data = undefined;
        this._uuid = randomstring.generate({ length: 1, charset: "alphabetic" }) +
            randomstring.generate({ length: 3, charset: "alphanumeric" });
        this._cachedDefaultOption = this.defaultOptions();
        this._renderHelper = new RenderHelper(this);
    }
    PrototypeElement.prototype.defaultOptions = function () {
        return {
            _dynamicSizeGetter: null,
        };
    };
    /**
     * Returns the attribute name for tag content. For example, "_text" for <Text>.
     */
    PrototypeElement.attributeForTagContent = function () {
        return null;
    };
    /**
     * Parse the string value for an attribute.
     */
    // tsilnt:disable-next-line
    PrototypeElement.parseAttributeValue = function (attr, value) {
        switch (attr) {
            case "_anchor":
            case "_relatedTo":
                var result = value.split("|").map(function (x) { return ({
                    Top: 2 /* Top */, Bottom: 8 /* Bottom */, Middle: 4 /* Middle */,
                    Left: 16 /* Left */, Right: 64 /* Right */, Center: 32 /* Center */,
                })[x]; }).reduce(function (prev, curr) { return prev | curr; });
                if (typeof result !== "number") {
                    logger.error("Unknown value " + value + " for _relatedTo!");
                }
                return result;
            case "_display":
                return utils_1.parseBoolean(value);
            default:
                return value;
        }
    };
    PrototypeElement.initializeFromTemplate = function (obj) {
        var args = [];
        for (var _a = 1; _a < arguments.length; _a++) {
            args[_a - 1] = arguments[_a];
        }
        return;
    };
    PrototypeElement.prototype.applyAll = function (func, skipHidden) {
        if (skipHidden === void 0) { skipHidden = true; }
        var stop = func.call(null, this);
        if (stop || (skipHidden && this.noNeedToRender)) {
            return;
        }
        if (this instanceof component_1.Component) {
            this.elements.forEach(function (x) { return x.applyAll(func, skipHidden); });
        }
    };
    PrototypeElement.prototype.appendSelf = function (checkAppended) {
        if (checkAppended === void 0) { checkAppended = true; }
        if (checkAppended && this._appended) {
            return;
        }
        if (!this.parent) {
            return;
        }
        if (this._isEnterAppend) {
            if (!this.supportsGroup) {
                throw new Error(this.constructor.nodeName + " cannot be appended as group!");
            }
            if (!(this._data && (this._data instanceof Array || typeof this._data === "function"))) {
                console.error(this);
                throw new Error("You must supply an array for a grouped element.");
            }
            // append
            this.dom = this.parent.dom
                .selectAll("." + this._uuid)
                .data(this._data) // should be a ts limitation
                .enter()
                .append(this.constructor.nodeName);
        }
        else {
            this.dom = this.parent.dom
                .append(this.constructor.nodeName);
            if (typeof this._data !== "undefined") {
                this.dom.datum(this._data);
            }
        }
        this.dom.classed(this._uuid, true);
        this._appended = true;
        this.postAppend();
    };
    PrototypeElement.prototype.assignData = function () {
        if (this._dataAssigned) {
            return;
        }
        // FIXME: should remove the previous registered listener if needed
        if (this.parent) {
            this.$visualizer = this.parent.$visualizer;
        }
        if (typeof this._data === "function") {
            this._data = this._data.bind(this);
        }
        else if (typeof this._data === "object" &&
            typeof this._data.__dataKey__ === "string") {
            var dataKey = this._data.__dataKey__;
            // using data binding
            this.__dataKey__ = dataKey;
            var data = _.get(this.$visualizer.data, dataKey);
            if (typeof data === "undefined") {
                console.warn("Data for data key " + dataKey + " is undefined");
                this._data = this._isEnterAppend ? [] : null;
            }
            else {
                this._data = data;
                global_1.default.currentVisualizer.registerListener(dataKey.split(".")[0], this);
            }
        }
        this._dataAssigned = true;
    };
    PrototypeElement.prototype.cacheGeometry = function () {
        var _this = this;
        if (this.noNeedToRender) {
            return;
        }
        this.domEach(function (d, _i, g, i) {
            _this._computedSize[i] = {};
            _this._intrinsicSize[i] = {};
            var geometry = {};
            _this._geometryKeys.forEach(function (op) {
                var geo = _this.getOption("_" + op, d, _i, g);
                if (typeof geo === "number") {
                    geo = geometry_1.GeometryValue.create(geo);
                }
                else if (typeof geo === "string") {
                    geo = geometry_1.GeometryValue.parse(geo);
                }
                geometry[op] = geo;
            });
            _this._cachedGeometry[i] = geometry;
            _this._cachedAnchor[i] = _this.getOption("_anchor", d, _i, g);
        });
    };
    PrototypeElement.prototype.computeIntrinsicSize = function () {
        var _this = this;
        if (this.noNeedToRender) {
            return;
        }
        this.domEach(function (d, _, g, i) {
            var geometry = _this._cachedGeometry[i];
            _this._geometryKeys.forEach(function (op) {
                var geo = geometry[op];
                if (geo._auto || geo.unit === geometry_1.GeometryUnit.Percent) {
                    return;
                }
                _this._intrinsicSize[i][op] =
                    _this._computedSize[i][op] = geo.value + geo.offset;
            });
        });
    };
    PrototypeElement.prototype.computeExtrinsicSize = function () {
        var _this = this;
        if (this.noNeedToRender) {
            return;
        }
        this.domEach(function (d, _i, g, i) {
            _this._geometryKeys.forEach(function (op) {
                var geo = _this._cachedGeometry[i][op];
                if (geo.unit === geometry_1.GeometryUnit.Percent) {
                    var parentSize = void 0;
                    if (_this.parent) {
                        parentSize = _this.parent.computedSize(g[_i].parentNode);
                    }
                    else {
                        parentSize = _this.$visualizer.size;
                    }
                    var parentOp = { x: "width", y: "height" }[op] || op;
                    if (parentSize) {
                        _this._computedSize[i][op] = geo.value * 0.01 * parentSize[parentOp] + geo.offset;
                    }
                    else {
                        _this._computedSize[i][op] = 0;
                    }
                }
                else {
                    var intrinsicSize = _this._intrinsicSize[i][op];
                    _this._computedSize[i][op] = intrinsicSize === undefined ? 0 : intrinsicSize;
                }
            });
        });
    };
    PrototypeElement.prototype.maxIntrinsicSize = function (horizontal, useComputedSize) {
        if (useComputedSize === void 0) { useComputedSize = false; }
        var startOp;
        var op;
        if (horizontal) {
            startOp = "x";
            op = "width";
        }
        else {
            startOp = "y";
            op = "height";
        }
        var size = useComputedSize ? this._computedSize : this._intrinsicSize;
        return _.max(size.map(function (x) { return ((x[startOp] || 0) + (x[op] || 0)); }));
    };
    PrototypeElement.prototype.findFixedParent = function () {
        return this.parent.findFixedParent();
    };
    PrototypeElement.prototype.prepare = function () { };
    PrototypeElement.prototype.setup = function () {
        if (this.__stopRender) {
            return;
        }
        var parentIsHidden = this.parent && this.parent.noNeedToRender;
        var display = !parentIsHidden && this.getOption("_display");
        this.noNeedToRender = !display;
        if (display && !this._appended) {
            this.assignData();
            this.appendSelf();
            // first setup
            this.attachListeners();
        }
        else if (!display && this._appended) {
            this.remove();
            this.resetDirtyOptions();
        }
        else if (!this._dataAssigned) {
            this.assignData();
        }
        if (!display) {
            return;
        }
        this.groupLocals = {};
        this.dom.attr("transform", "");
        var beforeAll;
        // tslint:disable-next-line
        if (beforeAll = this._options["_$beforeAll"]) {
            this._callRender(function (node, d, i, g, h) {
                beforeAll.call(node, d, i, g, h);
            });
        }
        this.cacheGeometry();
    };
    PrototypeElement.prototype.postAppend = function () {
        this.setAncestor();
        this.setupZoom();
        this.setupActiveClass();
        this.setupTooltip();
        this.callCallback(this._options._$postAppend);
    };
    PrototypeElement.prototype.setAncestor = function () {
        if (typeof this.parent === "undefined" || this._isEnterAppend) {
            this.ancestor = this;
        }
        else {
            this.ancestor = this.parent.ancestor;
        }
    };
    PrototypeElement.prototype.setupZoom = function () {
        var _this = this;
        // behavior
        var zoomOpt = this._options._zoom;
        if (zoomOpt) {
            var key_1 = _.find(["x", "y", "xy"], function (k) { return !!zoomOpt[k]; });
            if (typeof key_1 !== "string") {
                logger.error("You must set x, y or xy for zoom behavior.");
            }
            var hasScale = void 0;
            if (typeof zoomOpt.scale === "function") {
                hasScale = true;
                this._zoomScale = zoomOpt.scale.copy();
            }
            else {
                hasScale = false;
            }
            // FIXME: Rect: use _width
            var width = this._getGeometry("_width", true);
            var height = this._getGeometry("_height", false);
            var scaleStrSplitted = zoomOpt[key_1].split(":").map(function (x) { return parseFloat(x); });
            if (typeof zoomOpt.object === "function") {
                this._zoom = zoomOpt.object;
            }
            else {
                this._zoom = d3.zoom();
            }
            this._zoom.extent([[0, 0], [width, height]])
                .scaleExtent(scaleStrSplitted)
                .translateExtent([[0, 0], [width, height]]);
            if (hasScale) {
                this._zoom.on("zoom", function (d, i, g) {
                    if (d3.event.sourceEvent && d3.event.sourceEvent.type === "brush") {
                        return;
                    } // ignore zoom-by-brush
                    var f;
                    switch (key_1) {
                        case "x":
                            f = d3.event.transform.rescaleX;
                            break;
                        case "y":
                            f = d3.event.transform.rescaleY;
                            break;
                        case "xy":
                            f = d3.event.transform.rescaleX;
                            break;
                    }
                    zoomOpt.scale.domain(f.call(d3.event.transform, _this._zoomScale).domain());
                    zoomOpt.callback.call(_this, d, i, g);
                });
            }
            else if (!zoomOpt.custom) {
                this._zoom.on("zoom", function (d, i, g) {
                    if (d3.event.sourceEvent && d3.event.sourceEvent.type === "brush") {
                        return;
                    } // ignore zoom-by-brush
                    _this.setZoomTransform(d3.event.transform, d, i, g);
                    zoomOpt.callback.call(_this, d, i, g);
                });
            }
            this.dom.call(this._zoom);
        }
    };
    PrototypeElement.prototype.setupActiveClass = function () {
        var activeClass = this._options._activeClass;
        if (activeClass) {
            this.dom
                .on("mouseenter.bvd3", function (d, i, g) { return d3.select(g[i]).classed(activeClass, true); })
                .on("mouseleave.bvd3", function (d, i, g) { return d3.select(g[i]).classed(activeClass, false); });
        }
    };
    PrototypeElement.prototype.setupTooltip = function () {
        var tipRenderFunc = this._options._tooltip;
        if (tipRenderFunc && typeof tipRenderFunc === "function") {
            __1.tooltip.create(this._uuid).attachTo(this.dom).renderHTML(tipRenderFunc);
        }
    };
    // APIs
    PrototypeElement.prototype.reset = function () {
        this._appended = false;
        this._dataAssigned = false;
        this._rendered = false;
        this.__stopRender = false;
        this._resetSize();
        this.resetDirtyOptions();
    };
    PrototypeElement.prototype._resetSize = function () {
        this._cachedGeometry = [];
        this._intrinsicSize = [];
        this._computedSize = [];
    };
    PrototypeElement.prototype.wipe = function () {
        this._appended = false;
        if (this.dom) {
            this.dom.remove();
        }
        if (this instanceof component_1.Component) {
            this.elements.forEach(function (x) { return x.wipe(); });
        }
    };
    PrototypeElement.prototype.options = function () {
        var _a;
        var _this = this;
        var opt = typeof arguments[0] === "string" ? (_a = {}, _a[arguments[0]] = arguments[1], _a) : arguments[0];
        var dataUpdated = false;
        _.forOwn(opt, function (value, key) {
            if (key === "_data" || key === "_datum") {
                _this._data = value;
                dataUpdated = true;
                return;
            }
            else if (key === "_id") {
                if (typeof value !== "string") {
                    console.error("id:", value);
                    throw new Error("Element id must be a string");
                }
                _this.id = value;
                return;
            }
            else if (key === "_on") {
                _this._options._on = __assign(__assign({}, (_this._options._on || {})), opt[key]);
            }
            if (typeof value === "object" && value !== null && key !== "_on") {
                // tslint:disable-next-line
                var objKey = opt[key]["__objectKey__"];
                if (typeof objKey === "string") {
                    global_1.default.currentVisualizer.registerListener(objKey, _this);
                    opt[key] = global_1.default.currentVisualizer.data[objKey];
                }
                else if (typeof opt[key] === "object") {
                    _.forOwn(opt[key], function (v, k) {
                        // tslint:disable-next-line
                        var nestedObjKey = v["__objectKey__"];
                        if (typeof nestedObjKey === "string") {
                            global_1.default.currentVisualizer.registerListener(nestedObjKey, _this);
                            opt[key][k] = global_1.default.currentVisualizer.data[nestedObjKey];
                        }
                    });
                }
            }
            _this._options[key] = opt[key];
            _this._dirtyOptions.add(key);
        });
        if (dataUpdated) {
            if (this._appended) {
                if (this._isEnterAppend) {
                    this.dom.data(this._data);
                }
                else {
                    this.dom.datum(this._data);
                }
            }
        }
        this._postOptionsUpdate(this._dirtyOptions);
        return this;
    };
    PrototypeElement.prototype.data = function () {
        if (!this.supportsGroup) {
            console.error(this.constructor.nodeName + " doesn't support group, please use datum() instead.");
            return;
        }
        if (arguments.length === 0) {
            return this._data;
        }
        else {
            if (this.$visualizer) {
                this.$visualizer._disableDataUpdate = true;
            }
            var d = arguments[0];
            this._data = d instanceof Function ? d.bind(this) : d;
            if (typeof this.__dataKey__ === "string") {
                _.set(this.$visualizer.data, this.__dataKey__, d);
            }
            for (var key in this._options) {
                if (typeof this._options[key] === "function") {
                    this.markDirty(key);
                }
            }
            this._dataAssigned = false;
            this._postDataUpdate();
            if (this.$visualizer) {
                this.$visualizer._disableDataUpdate = false;
            }
            return this;
        }
    };
    Object.defineProperty(PrototypeElement.prototype, "datum", {
        get: function () {
            var dataKey = this._data.__dataKey__;
            if (typeof dataKey === "string") {
                var data = this.$visualizer.data[dataKey];
                if (typeof data === "undefined") {
                    logger.error("Data for data key " + dataKey + " is undefined");
                    return null;
                }
                else {
                    return data;
                }
            }
            else {
                if (this.dom) {
                    return this.dom.datum() || this._data;
                }
                else {
                    return this._data;
                }
            }
        },
        set: function (d) {
            function assignDatum(el, d) {
                if (el.dom) {
                    el.dom.datum(d);
                }
                if (el instanceof component_1.Component) {
                    el.elements.forEach(function (e) { return assignDatum(e, d); });
                }
            }
            if (this.$visualizer) {
                this.$visualizer._disableDataUpdate = true;
            }
            this._data = d;
            if (typeof this.__dataKey__ === "string") {
                _.set(this.$visualizer.data, this.__dataKey__, d);
            }
            if (this._appended) {
                assignDatum(this, d);
            }
            for (var key in this._options) {
                if (typeof this._options[key] === "function") {
                    this.markDirty(key);
                }
            }
            this._dataAssigned = false;
            this._postDatumUpdate();
            if (this.$visualizer) {
                this.$visualizer._disableDataUpdate = false;
            }
        },
        enumerable: true,
        configurable: true
    });
    PrototypeElement.prototype.setDatum = function (d) {
        this.datum = d;
        return this;
    };
    PrototypeElement.prototype.updateData = function () {
        if (typeof this.__dataKey__ === "string") {
            var d = _.get(this.$visualizer.data, this.__dataKey__);
            if (this._isEnterAppend) {
                this.data(d);
            }
            else {
                this.datum = d;
            }
        }
        var check = function (el) {
            if (typeof el[el._isEnterAppend ? "_data" : "_datum"] === "function") {
                el.remove();
            }
            else {
                el.resetDirtyOptions();
                if (el instanceof component_1.Component) {
                    el.elements.forEach(check);
                }
            }
        };
        check(this);
        return this;
    };
    PrototypeElement.prototype.remove = function (isRoot) {
        if (isRoot === void 0) { isRoot = true; }
        if (typeof this.parent === "undefined" || !this._appended) {
            return;
        }
        if (isRoot) {
            this.dom.html("");
        }
        this.dom = undefined;
        this._appended = false;
        this.resetDirtyOptions();
        if (this instanceof component_1.Component) {
            this.elements.forEach(function (x) { return x.remove(false); });
        }
    };
    PrototypeElement.prototype.markDirty = function () {
        var _this = this;
        if (arguments[0] instanceof Array) {
            arguments[0].forEach(function (element) {
                _this._dirtyOptions.add(element);
            });
        }
        else {
            this._dirtyOptions.add(arguments[0]);
        }
        return this;
    };
    PrototypeElement.prototype._preRender = function () {
        if (this.noNeedToRender) {
            return false;
        }
        this.preRender();
        return true;
    };
    PrototypeElement.prototype._postRender = function () {
        this.postRender();
        this._dirtyOptions.clear();
        this._rendered = true;
    };
    PrototypeElement.prototype._getRenderHelper = function (d, i, g) {
        this._renderHelper.data = d;
        this._renderHelper.index = i;
        this._renderHelper.group = g;
        return this._renderHelper;
    };
    PrototypeElement.prototype._callRender = function (renderFunc) {
        var _this = this;
        this.dom.each(function (d, i, g) {
            renderFunc.call(_this, d3_1.select(g[i]), d, i, g, _this._getRenderHelper(d, i, g));
        });
    };
    PrototypeElement.prototype._render = function () {
        var _this = this;
        this._callRender(this.renderElement);
        if (this._options._relatedTo !== null) {
            this._callRender(function (node, d, i, g, h) {
                var relatedTo = h.op("_relatedTo");
                if (relatedTo !== null) {
                    var currTransform = node.attr("transform") || "";
                    var pt = _this.parent.pointFromAnchor(relatedTo, g[i].parentNode);
                    node.attr("transform", tool.translate(pt.x, pt.y) + " " + currTransform);
                }
            });
        }
    };
    // tslint:disable-next-line
    PrototypeElement.prototype.preRender = function () { };
    // tslint:disable-next-line
    PrototypeElement.prototype.postRender = function () { };
    PrototypeElement.prototype._postDataUpdate = function () {
        if (this._appended) {
            this.wipe();
            this.dom.classed(this._uuid, true);
            this.resetDirtyOptions();
        }
        this.postDataUpdate();
    };
    PrototypeElement.prototype._postDatumUpdate = function () {
        this.postDatumUpdate();
    };
    PrototypeElement.prototype._postOptionsUpdate = function (dirtyOptions) {
        this.postOptionsUpdate(dirtyOptions);
    };
    // tslint:disable-next-line
    PrototypeElement.prototype.postDataUpdate = function () { };
    // tslint:disable-next-line
    PrototypeElement.prototype.postDatumUpdate = function () { };
    // tslint:disable-next-line
    PrototypeElement.prototype.postOptionsUpdate = function (dirtyOptions) { };
    PrototypeElement.prototype.attachListeners = function () {
        var _this = this;
        // legacy syntax
        for (var key in eventNameMap) {
            if (!this._options[key]) {
                continue;
            }
            this.dom.on(eventNameMap[key], this._options[key]);
        }
        // .on
        _.forOwn(this._options._on, function (callback, event) {
            _this.dom.on(event, callback);
        });
    };
    // utils
    PrototypeElement.prototype.domEach = function (callback) {
        var idx = -1;
        for (var groups = this.dom._groups, j = 0, m = groups.length; j < m; ++j) {
            for (var group = groups[j], i = 0, n = group.length, node = void 0; i < n; ++i) {
                if (node = group[i]) {
                    callback.call(node, node.__data__, i, group, ++idx);
                }
            }
        }
        return this;
    };
    PrototypeElement.prototype.hide = function () {
        this.dom.attr("visibility", "hidden");
    };
    PrototypeElement.prototype.show = function () {
        this.dom.attr("visibility", "visible");
    };
    PrototypeElement.prototype.callCallback = function (cb) {
        if (!cb) {
            return;
        }
        cb.call(this, this);
    };
    PrototypeElement.prototype.getOption = function (optKey, d, i, g, isFunc) {
        var opt = this._options[optKey];
        if (typeof opt === "undefined") {
            opt = this._cachedDefaultOption[optKey];
            if (typeof opt === "undefined") {
                console.warn("Undefined value for option '" + optKey + "'!", this);
            }
        }
        if (typeof isFunc !== "boolean") {
            var funcKey = optKey + "_isFunc";
            var isTransformer = this._options[funcKey];
            if (typeof isTransformer === "undefined") {
                isTransformer = this._cachedDefaultOption[funcKey];
            }
            if (typeof isTransformer === "boolean") {
                isFunc = !isTransformer;
            }
        }
        if (typeof opt === "function" && !isFunc) {
            if (typeof d === "undefined") {
                d = this._appended ? (this.dom.datum() || { _isEmpty: true }) : null;
            }
            return opt.call(this, d, i, g, this._getRenderHelper(d, i, g));
        }
        return opt;
    };
    PrototypeElement.prototype.getRole = function () {
        return this._options._role;
    };
    PrototypeElement.prototype._getGeometry = function (optKey, horizontal, d, i, g) {
        var inGroup = arguments.length > 2;
        var geo = parseGeometryValue(inGroup ? this.getOption(optKey, d, i, g) : this.getOption(optKey));
        var isPercent = geo.unit === geometry_1.GeometryUnit.Percent;
        if (isPercent) {
            var parentBounds = void 0;
            var attr = horizontal ? "width" : "height";
            if (this.parent) {
                parentBounds = this.parent.computedSize((inGroup ? g[i].parentNode : this.parent.dom.node()));
            }
            else {
                parentBounds = {
                    x: 0, y: 0,
                    width: global_1.default.currentVisualizer.size.width,
                    height: global_1.default.currentVisualizer.size.height,
                };
            }
            return (geo.value / 100.0) * parentBounds[attr] + geo.offset;
        }
        return geo.value;
    };
    PrototypeElement.prototype._isDirty = function () {
        var _this = this;
        var checkDirty = function (option) {
            return typeof _this._options[option] !== "undefined" && _this._dirtyOptions.has(option);
        };
        if (arguments[0] instanceof Array) {
            var options = arguments[0];
            return _.some(options, checkDirty);
        }
        else {
            var option = arguments[0];
            return checkDirty(option);
        }
    };
    PrototypeElement.prototype._dirtySVGOptions = function () {
        return Array.from(this._dirtyOptions).filter(function (opt) { return opt[0] !== "_"; });
    };
    PrototypeElement.prototype.resetDirtyOptions = function () {
        var _this = this;
        var opts = Object.keys(this._options).filter(function (x) { return x !== "_brush"; });
        opts.forEach(function (o) { return _this._dirtyOptions.add(o); });
        if (this instanceof component_1.Component) {
            this.elements.forEach(function (x) { return x.resetDirtyOptions(); });
        }
    };
    PrototypeElement.prototype.setZoomTransform = function (t, d, i, group) {
        d3.select(group[i]).attr("transform", "translate(" + d3.event.translate + ")scale(" + d3.event.scale + ")");
    };
    Object.defineProperty(PrototypeElement.prototype, "absolutePosition", {
        get: function () {
            var svgRect = this.$visualizer.svg.node().getBoundingClientRect();
            var rect = this.dom.node().getBoundingClientRect();
            return { x: rect.x - svgRect.x, y: rect.y - svgRect.y };
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PrototypeElement.prototype, "__info__", {
        get: function () {
            var formatNumber = function (n) {
                return typeof n === "number" ?
                    Number.isInteger(n) ? n : n.toFixed(1) :
                    "?";
            };
            var info = "" + this.constructor.name;
            if (this._isEnterAppend) {
                info += ".group";
            }
            info += "[";
            info += this._dataAssigned ? "D" : "-";
            info += this._appended ? "A" : "-";
            info += "]";
            info += " (" + this._uuid + ")";
            if (this.noNeedToRender) {
                info += "X";
            }
            if (!this._isEnterAppend && this._appended) {
                var s = this._computedSize[0];
                info += " (";
                if (s) {
                    info += formatNumber(s.width) + "x" + formatNumber(s.height) + " @ " + formatNumber(s.x) + "," + formatNumber(s.y) + ")";
                }
                else {
                    info += "??)";
                }
                if (this.hasFixedSize) {
                    info += "*";
                }
                if (this.hasFlexibleSize) {
                    info += "~";
                }
            }
            if (this.__dataKey__) {
                info += " @" + this.__dataKey__;
            }
            if (this.$visualizer && this === this.$visualizer.__activeComponent__) {
                info += " <--";
            }
            return info;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PrototypeElement.prototype, "__tree__", {
        get: function () {
            return this.__info__;
        },
        enumerable: true,
        configurable: true
    });
    PrototypeElement.prototype.__pos__ = function () {
        this.$visualizer.__pos__(this);
    };
    return PrototypeElement;
}());
exports.PrototypeElement = PrototypeElement;
//# sourceMappingURL=prototype.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/bvd3/dist/element/prototype.js
// module id = 1049
// module chunks = 0 1